import React, { useRef, useEffect, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { MapPin, X, RotateCw, Move, Eye } from 'lucide-react';
import 'maplibre-gl/dist/maplibre-gl.css';

const Map3DView = ({ properties, onPropertySelect, selectedProperty }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [popupInfo, setPopupInfo] = useState(null);
  const markersRef = useRef([]);
  const [controlsInfo, setControlsInfo] = useState(true);
  
  // Maptiler API key for real Dallas building data
  const MAPTILER_API_KEY = 'gGec581DgZdczOZcqbuR';

  // Generate comprehensive Dallas downtown building grid
  const generateDallasBuildings = () => {
    const buildings = [];
    const centerLat = 32.7767;
    const centerLng = -96.7970;
    
    // Create a dense grid of buildings across downtown Dallas
    for (let i = -15; i < 15; i++) {
      for (let j = -15; j < 15; j++) {
        const latOffset = i * 0.0008;
        const lngOffset = j * 0.0008;
        
        // Vary building heights based on distance from center (taller in center)
        const distanceFromCenter = Math.sqrt(i * i + j * j);
        const baseHeight = Math.max(50, 300 - distanceFromCenter * 15);
        const height = baseHeight + Math.random() * 100;
        
        // Create building footprint
        const size = 0.0003 + Math.random() * 0.0002;
        
        buildings.push({
          type: 'Feature',
          properties: { 
            height: height, 
            base_height: 0,
            id: `building-${i}-${j}`
          },
          geometry: {
            type: 'Polygon',
            coordinates: [[
              [centerLng + lngOffset, centerLat + latOffset],
              [centerLng + lngOffset + size, centerLat + latOffset],
              [centerLng + lngOffset + size, centerLat + latOffset + size],
              [centerLng + lngOffset, centerLat + latOffset + size],
              [centerLng + lngOffset, centerLat + latOffset]
            ]]
          }
        });
      }
    }
    
    return buildings;
  };

  // Generate street grid
  const generateStreetGrid = () => {
    const streets = [];
    const centerLat = 32.7767;
    const centerLng = -96.7970;
    
    // Horizontal streets
    for (let i = -15; i < 15; i++) {
      const latOffset = i * 0.0008;
      streets.push({
        type: 'Feature',
        properties: { type: 'street' },
        geometry: {
          type: 'LineString',
          coordinates: [
            [centerLng - 0.015, centerLat + latOffset],
            [centerLng + 0.015, centerLat + latOffset]
          ]
        }
      });
    }
    
    // Vertical streets
    for (let j = -15; j < 15; j++) {
      const lngOffset = j * 0.0008;
      streets.push({
        type: 'Feature',
        properties: { type: 'street' },
        geometry: {
          type: 'LineString',
          coordinates: [
            [centerLng + lngOffset, centerLat - 0.015],
            [centerLng + lngOffset, centerLat + 0.015]
          ]
        }
      });
    }
    
    return streets;
  };

  useEffect(() => {
    if (map.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: `https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_API_KEY}`, // Use streets style which has building data
      center: [-96.7970, 32.7767], // Downtown Dallas center
      zoom: 15.5, // Closer zoom to see buildings better
      pitch: 60,
      bearing: -25,
      antialias: true,
      // Enable full rotation and pitch control
      dragRotate: true,
      pitchWithRotate: true,
      touchZoomRotate: true
    });

    // Add navigation controls with compass for rotation
    const nav = new maplibregl.NavigationControl({
      visualizePitch: true,
      showCompass: true,
      showZoom: true
    });
    map.current.addControl(nav, 'top-right');

    // Add full screen control
    map.current.addControl(new maplibregl.FullscreenControl(), 'top-right');

    map.current.on('load', () => {
      // Wait for style to fully load
      setTimeout(() => {
        const style = map.current.getStyle();
        const layers = style.layers;
        
        // Find the correct vector tile source name (Maptiler uses different names)
        const vectorSource = Object.keys(style.sources).find(key => 
          style.sources[key].type === 'vector' && 
          style.sources[key].url && 
          style.sources[key].url.includes('maptiler')
        );
        
        console.log('✅ Found vector source:', vectorSource);
        console.log('✅ Available sources:', Object.keys(style.sources));
        
        if (!vectorSource) {
          console.error('❌ No suitable vector source found');
          return;
        }
        
        // Find the label layer to insert buildings before it
        const labelLayerId = layers.find(
          (layer) => layer.type === 'symbol' && layer.layout && layer.layout['text-field']
        )?.id;

        // Add 3D buildings layer using real Maptiler/OSM data
        try {
          map.current.addLayer({
            'id': '3d-buildings-white',
            'type': 'fill-extrusion',
            'source': vectorSource,
            'source-layer': 'building',
            'minzoom': 13,
            'filter': ['==', ['geometry-type'], 'Polygon'],
            'paint': {
              'fill-extrusion-color': [
                'interpolate',
                ['linear'],
                ['coalesce', ['get', 'render_height'], ['*', ['to-number', ['get', 'building:levels'], 3], 3.5]],
                0, '#ffffff',
                50, '#f7f7f7',
                100, '#eeeeee',
                200, '#e5e5e5',
                300, '#dddddd',
                400, '#cccccc'
              ],
              'fill-extrusion-height': [
                'interpolate',
                ['linear'],
                ['zoom'],
                13, 0,
                13.5, ['coalesce', ['get', 'render_height'], ['*', ['to-number', ['get', 'building:levels'], 3], 3.5], 15]
              ],
              'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
              'fill-extrusion-opacity': 0.95
            }
          }, labelLayerId);
          
          console.log('✅ 3D buildings layer added successfully');
        } catch (error) {
          console.error('❌ Error adding 3D buildings:', error);
        }

        // Enhanced lighting for dramatic 3D effect
        if (map.current.setLight) {
          map.current.setLight({
            anchor: 'viewport',
            color: '#ffffff',
            intensity: 0.9,
            position: [1.5, 210, 50]
          });
        }
      }, 1000); // Wait 1 second for style to fully load
    });

    // Enable keyboard controls for rotation and pitch
    const handleKeyDown = (e) => {
      if (!map.current) return;
      
      const rotateAmount = 10;
      const pitchAmount = 5;
      
      switch(e.key) {
        case 'ArrowLeft':
          map.current.setBearing(map.current.getBearing() - rotateAmount);
          break;
        case 'ArrowRight':
          map.current.setBearing(map.current.getBearing() + rotateAmount);
          break;
        case 'ArrowUp':
          map.current.setPitch(Math.min(85, map.current.getPitch() + pitchAmount));
          break;
        case 'ArrowDown':
          map.current.setPitch(Math.max(0, map.current.getPitch() - pitchAmount));
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Hide controls info after 5 seconds
    setTimeout(() => setControlsInfo(false), 5000);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Add markers when properties change
  useEffect(() => {
    if (!map.current) return;

    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    const propertyGroups = properties.reduce((groups, property) => {
      const key = `${property.lat.toFixed(3)}-${property.lng.toFixed(3)}`;
      if (!groups[key]) {
        groups[key] = {
          lat: property.lat,
          lng: property.lng,
          properties: [],
          type: property.type
        };
      }
      groups[key].properties.push(property);
      return groups;
    }, {});

    const groups = Object.values(propertyGroups);

    const getMarkerIcon = (type) => {
      if (type.includes('Office')) return '🏢';
      if (type.includes('Retail')) return '🏪';
      if (type.includes('Industrial')) return '🏭';
      return '🏘️';
    };

    const getMarkerColor = (type) => {
      if (type.includes('Office')) return '#3b82f6';
      if (type.includes('Retail')) return '#a855f7';
      if (type.includes('Industrial')) return '#f97316';
      return '#14b8a6';
    };

    groups.forEach(group => {
      const el = document.createElement('div');
      el.style.cursor = 'pointer';
      el.innerHTML = `
        <div class="relative transform hover:scale-110 transition-transform" style="width: 64px; height: 64px;">
          <div style="width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(to bottom right, #374151, #111827); display: flex; align-items: center; justify-content: center; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04); border: 2px solid rgba(255, 255, 255, 0.3);">
            <div style="width: 48px; height: 48px; border-radius: 50%; background: ${getMarkerColor(group.type)}; display: flex; flex-direction: column; align-items: center; justify-content: center; color: white; font-size: 24px;">
              <span>${getMarkerIcon(group.type)}</span>
              <span style="font-size: 10px; font-weight: bold;">${group.properties.length}</span>
            </div>
          </div>
        </div>
      `;

      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([group.lng, group.lat])
        .addTo(map.current);

      el.addEventListener('click', () => {
        setPopupInfo(group);
      });

      markersRef.current.push(marker);
    });
  }, [properties]);

  // Rotation control functions
  const rotate360 = () => {
    if (!map.current) return;
    const currentBearing = map.current.getBearing();
    map.current.easeTo({
      bearing: currentBearing + 90,
      duration: 1000
    });
  };

  const resetView = () => {
    if (!map.current) return;
    map.current.easeTo({
      center: [-96.7970, 32.7767], // Downtown Dallas center
      zoom: 15.5, // Closer zoom to see buildings better
      pitch: 60,
      bearing: -25,
      duration: 1500
    });
  };

  const togglePitch = () => {
    if (!map.current) return;
    const currentPitch = map.current.getPitch();
    map.current.easeTo({
      pitch: currentPitch < 45 ? 75 : 30,
      duration: 1000
    });
  };

  return (
    <div className="relative w-full h-full">
      <div ref={mapContainer} className="w-full h-full rounded-xl" />

      {/* 360° Control Buttons */}
      <div className="absolute top-6 left-6 flex flex-col gap-2">
        <button
          onClick={rotate360}
          className="bg-white hover:bg-gray-100 text-black p-3 rounded-lg shadow-lg transition-colors flex items-center gap-2"
          title="Rotate 90°"
        >
          <RotateCw size={20} />
          <span className="text-sm font-semibold">Rotate 90°</span>
        </button>
        
        <button
          onClick={togglePitch}
          className="bg-white hover:bg-gray-100 text-black p-3 rounded-lg shadow-lg transition-colors flex items-center gap-2"
          title="Toggle Pitch"
        >
          <Eye size={20} />
          <span className="text-sm font-semibold">Tilt View</span>
        </button>
        
        <button
          onClick={resetView}
          className="bg-white hover:bg-gray-100 text-black p-3 rounded-lg shadow-lg transition-colors flex items-center gap-2"
          title="Reset View"
        >
          <Move size={20} />
          <span className="text-sm font-semibold">Reset</span>
        </button>
      </div>

      {/* Controls Info */}
      {controlsInfo && (
        <div className="absolute top-6 right-[100px] bg-black/80 backdrop-blur-md px-4 py-3 rounded-lg border border-white/20 max-w-xs">
          <h4 className="text-white text-sm font-bold mb-2">🎮 Controls</h4>
          <div className="text-xs text-gray-300 space-y-1">
            <p>• <strong>Drag:</strong> Rotate 360°</p>
            <p>• <strong>Right-click + Drag:</strong> Change pitch</p>
            <p>• <strong>Arrow Keys:</strong> Rotate & tilt</p>
            <p>• <strong>Scroll:</strong> Zoom in/out</p>
          </div>
        </div>
      )}

      {/* Property Popup Card */}
      {popupInfo && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
          <div className="bg-black/95 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/20 shadow-2xl w-[400px]">
            <button
              onClick={() => setPopupInfo(null)}
              className="absolute top-3 right-3 z-10 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors"
            >
              <X size={16} className="text-white" />
            </button>

            <div className="relative h-48 overflow-hidden">
              <img
                src={popupInfo.properties[0].image}
                alt={popupInfo.properties[0].name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-3 left-3 flex gap-2">
                <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                  <span className="text-sm">✏️</span>
                </div>
                <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                  <span className="text-sm">🔗</span>
                </div>
                <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                  <span className="text-sm">↗️</span>
                </div>
              </div>
            </div>

            <div className="p-4">
              <h3 className="text-xl font-bold text-white mb-1">
                {popupInfo.properties[0].name}
              </h3>
              <p className="text-sm text-gray-400 mb-3">
                {popupInfo.properties.length} {popupInfo.properties.length === 1 ? 'Property' : 'Properties'}
              </p>

              <button
                onClick={() => {
                  if (popupInfo.properties.length === 1) {
                    onPropertySelect(popupInfo.properties[0]);
                  }
                  setPopupInfo(null);
                }}
                className="w-full py-2.5 bg-white hover:bg-gray-200 text-black rounded-lg font-semibold transition-colors mb-3"
              >
                {popupInfo.properties.length === 1 ? 'View Details' : 'View All'}
              </button>

              <div className="flex items-start gap-2 text-sm text-gray-300">
                <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                <span>{popupInfo.properties[0].address}</span>
              </div>

              {popupInfo.properties.length === 1 && (
                <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-400">Price:</span>
                    <span className="text-white ml-1 font-semibold">${popupInfo.properties[0].price}M</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Area:</span>
                    <span className="text-white ml-1 font-semibold">{popupInfo.properties[0].area.toLocaleString()} sq ft</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Occupancy:</span>
                    <span className="text-white ml-1 font-semibold">{popupInfo.properties[0].occupancy}%</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Cap Rate:</span>
                    <span className="text-white ml-1 font-semibold">{popupInfo.properties[0].capRate}%</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 3D View Label */}
      <div className="absolute bottom-6 left-6 bg-black/80 backdrop-blur-md px-4 py-2 rounded-lg border border-white/20">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-white text-sm font-semibold">3D View Active • 360° Control</span>
        </div>
      </div>
    </div>
  );
};

export default Map3DView;
